import React, { useState, useEffect, useRef } from 'react';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import { fromLonLat } from 'ol/proj';
import { Style, Stroke, Circle as CircleStyle, Fill } from 'ol/style';
import './FlightDetails.css';

const FlightDetails = ({ flightNumber, onClose }) => {
  const [flight, setFlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    const loadFlight = async () => {
      try {
        const response = await fetch(`/api/flights/${flightNumber}`);
        const data = await response.json();
        if (data.success) {
          setFlight(data.data);
        } else {
          setError(data.message || 'Рейс не найден');
        }
      } catch (err) {
        setError('Ошибка загрузки данных рейса');
      } finally {
        setLoading(false);
      }
    };
    loadFlight();
  }, [flightNumber]);

  // Инициализация карты
  useEffect(() => {
    if (!flight || !flight.departure?.lat || !flight.arrival?.lat) return;
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setTarget(null);
      mapInstanceRef.current = null;
    }

    const depCoords = fromLonLat([flight.departure.lon, flight.departure.lat]);
    const arrCoords = fromLonLat([flight.arrival.lon, flight.arrival.lat]);

    const depFeature = new Feature({
      geometry: new Point(depCoords),
      name: flight.departure.icao
    });

    const arrFeature = new Feature({
      geometry: new Point(arrCoords),
      name: flight.arrival.icao
    });

    const routeFeature = new Feature({
      geometry: new LineString([depCoords, arrCoords])
    });

    const vectorSource = new VectorSource({
      features: [depFeature, arrFeature, routeFeature]
    });

    const vectorLayer = new VectorLayer({
      source: vectorSource,
      style: (feature) => {
        if (feature.getGeometry().getType() === 'LineString') {
          return new Style({
            stroke: new Stroke({
              color: '#F59E0B',
              width: 3,
              lineDash: [10, 10]
            })
          });
        } else {
          return new Style({
            image: new CircleStyle({
              radius: 8,
              fill: new Fill({ color: '#1E3A8A' }),
              stroke: new Stroke({ color: '#F59E0B', width: 2 })
            })
          });
        }
      }
    });

    const map = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new OSM({
            attributions: '© OpenStreetMap contributors'
          })
        }),
        vectorLayer
      ],
      view: new View({
        center: [
          (depCoords[0] + arrCoords[0]) / 2,
          (depCoords[1] + arrCoords[1]) / 2
        ],
        zoom: 4
      })
    });

    const extent = routeFeature.getGeometry().getExtent();
    map.getView().fit(extent, { 
      padding: [50, 50, 50, 50],
      maxZoom: 8
    });

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setTarget(null);
        mapInstanceRef.current = null;
      }
    };
  }, [flight]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-loading">
            <div className="spinner"></div>
            <p>Загрузка данных рейса...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !flight) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-error">
            <i className="fa fa-exclamation-circle"></i>
            <p>{error || 'Рейс не найден'}</p>
            <button onClick={onClose} className="btn-close-modal">Закрыть</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content flight-details-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        
        <div className="flight-details-header">
          <div className="flight-number">
            <span className="label">Рейс</span>
            <span className="value">{flight.number}</span>
          </div>
          <div className="flight-status">
            <span className={`status-badge ${flight.status_class}`}>
              {flight.display_status}
            </span>
          </div>
        </div>

        <div className="flight-route-info">
          <div className="airport-info departure">
            <div className="airport-code">{flight.departure.icao}</div>
            <div className="airport-city">{flight.departure.city || flight.departure.name}</div>
            <div className="airport-name">{flight.departure.name}</div>
            <div className="airport-label">Отправление</div>
          </div>
          
          <div className="route-arrow">
            <i className="fa fa-arrow-right"></i>
            <span className="distance">{flight.distance ? `${flight.distance} км` : ''}</span>
          </div>
          
          <div className="airport-info arrival">
            <div className="airport-code">{flight.arrival.icao}</div>
            <div className="airport-city">{flight.arrival.city || flight.arrival.name}</div>
            <div className="airport-name">{flight.arrival.name}</div>
            <div className="airport-label">Прибытие</div>
          </div>
        </div>

        {/* ОБНОВЛЁННАЯ СЕКЦИЯ С ВРЕМЕНЕМ */}
        <div className="flight-meta">
          <div className="meta-item">
            <i className="fa fa-clock-o"></i>
            <span>
              <span className="label">Вылет:</span> 
              {flight.departure.time || '—'}
            </span>
          </div>
          <div className="meta-item">
            <i className="fa fa-clock-o"></i>
            <span>
              <span className="label">Прилёт:</span> 
              {flight.arrival.time || '—'}
            </span>
          </div>
          {flight.duration && (
            <div className="meta-item">
              <i className="fa fa-hourglass-half"></i>
              <span>
                <span className="label">В пути:</span> 
                {flight.duration}
              </span>
            </div>
          )}
          <div className="meta-item">
            <i className="fa fa-plane"></i>
            <span>
              <span className="label">ВС:</span> 
              {flight.aircraft || 'Неизвестно'}
            </span>
          </div>
          <div className="meta-item">
            <i className="fa fa-building"></i>
            <span>
              <span className="label">Оператор:</span> 
              {flight.operator?.name || 'Pulkovo Air'}
            </span>
          </div>
        </div>

        <div className="flight-map-container">
          <div ref={mapRef} className="flight-map"></div>
        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="btn-close-modal">Закрыть</button>
        </div>
      </div>
    </div>
  );
};

export default FlightDetails;